/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印批量编辑工具函数
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-12 14:08:42
 */

import { WatermarkExtractionStatus } from '../../context/WatermarkBatchContext'
import { WatermarkApplyStatus } from '../../hooks/useWatermarkApply'
import { TestDataItem } from '../hooks/useCSVUpload'

/**
 * 移除 URL 中的 x-oss-process=image/resize,w_300 参数
 */
export const removeOssResizeParam = (url: string): string => {
  if (!url) return url
  // 移除 x-oss-process=image/resize,w_300 参数
  // 处理 ?x-oss-process=image/resize,w_300 或 &x-oss-process=image/resize,w_300
  let result = url.replace(/[?&]x-oss-process=image\/resize,w_300/g, '')
  // 如果移除后 ? 后面直接是 & 或结尾，移除 ?
  result = result.replace(/\?(&|$)/, '')
  // 如果移除后查询字符串部分开头是 &，替换为 ?
  const queryIndex = result.indexOf('?')
  if (queryIndex !== -1 && result[queryIndex + 1] === '&') {
    result =
      result.substring(0, queryIndex + 1) + result.substring(queryIndex + 2)
  }
  return result
}

/**
 * 添加阿里云 OSS 图片处理参数（宽度控制）
 * 会自动移除旧的 x-oss-process 参数，然后添加新的参数
 * @param url 原始图片 URL
 * @param width 目标宽度，默认 200
 * @returns 添加了处理参数的 URL
 */
export const addOssResizeParam = (url: string, width: number = 200): string => {
  if (!url) return url

  // 先移除可能存在的旧参数
  let cleanUrl = url.replace(/[?&]x-oss-process=[^&]*/g, '')

  // 如果移除后 ? 后面直接是 & 或结尾，移除 ?
  cleanUrl = cleanUrl.replace(/\?(&|$)/, '')
  // 如果移除后查询字符串部分开头是 &，替换为 ?
  const queryIndex = cleanUrl.indexOf('?')
  if (queryIndex !== -1 && cleanUrl[queryIndex + 1] === '&') {
    cleanUrl =
      cleanUrl.substring(0, queryIndex + 1) + cleanUrl.substring(queryIndex + 2)
  }

  // 确定使用 ? 还是 &
  const hasQuery = cleanUrl.includes('?')
  const separator = hasQuery ? '&' : '?'

  // 添加新的处理参数
  return `${cleanUrl}${separator}x-oss-process=image/resize,w_${width}`
}

/**
 * 解析 diff 数据，分类为新增、修改、删除
 */
export const parseDiffData = (diffStr: string) => {
  if (!diffStr) {
    return {
      newItems: [],
      modifiedItems: [],
      deletedItems: []
    }
  }

  try {
    const diffData = JSON.parse(diffStr)
    if (!Array.isArray(diffData)) {
      return {
        newItems: [],
        modifiedItems: [],
        deletedItems: []
      }
    }

    const newItems: Array<{
      item_id: number
      title: string
      content: string
    }> = []
    const modifiedItems: Array<{
      item_id: number
      title: string
      content: string
      originalContent: string
    }> = []
    const deletedItems: Array<{
      item_id: number
      title: string
      content: string
    }> = []

    diffData.forEach((item) => {
      // 排除 id 为 1 和 2 的项
      if (item.id === 1 || item.id === 2) {
        return
      }

      const diffItem = {
        item_id: item.id,
        title: item.title || '',
        content: item.content || '未设置内容',
        originalContent: item.old_content || '未设置内容'
      }

      if (item.editType === '新增') {
        newItems.push({
          item_id: diffItem.item_id,
          title: diffItem.title,
          content: diffItem.content
        })
      } else if (item.editType === '修改') {
        modifiedItems.push({
          item_id: diffItem.item_id,
          title: diffItem.title,
          content: diffItem.content,
          originalContent: diffItem.originalContent
        })
      } else if (item.editType === '删除') {
        deletedItems.push({
          item_id: diffItem.item_id,
          title: diffItem.title,
          content: diffItem.content
        })
      }
    })

    return {
      newItems,
      modifiedItems,
      deletedItems
    }
  } catch (error) {
    console.error('解析 diff 数据失败:', error)
    return {
      newItems: [],
      modifiedItems: [],
      deletedItems: []
    }
  }
}

/**
 * 获取提取状态显示文本和颜色
 */
export const getExtractionStatusDisplay = (
  status?: WatermarkExtractionStatus
): { text: string; color: string } => {
  switch (status) {
    case WatermarkExtractionStatus.EXTRACTING:
      return { text: '提取中', color: 'green' }
    case WatermarkExtractionStatus.COMPLETED:
      return { text: '提取完成', color: 'green' }
    case WatermarkExtractionStatus.EXTRACTING_TIMEOUT:
      return { text: '提取超时', color: 'yellow' }
    case WatermarkExtractionStatus.FAILED:
      return { text: '提取失败', color: 'red' }
    default:
      return { text: '未开始', color: 'gray' }
  }
}

/**
 * 获取处理状态显示信息（包括应用状态和提取状态）
 */
export const getProcessStatusInfo = (
  record: TestDataItem
): { text: string; color: string; errorMsg?: string } => {
  // 优先显示应用状态
  if (record.applyStatus !== undefined) {
    switch (record.applyStatus) {
      case WatermarkApplyStatus.EDITED:
        return { text: '处理完成', color: 'green' }
      case WatermarkApplyStatus.FAILED:
        return {
          text: '应用失败',
          color: 'red',
          errorMsg: record.applyShowReason || '应用失败'
        }
      case WatermarkApplyStatus.NO_CHANGES:
        return {
          text: '无修改项',
          color: 'orange',
          errorMsg: record.applyShowReason || '无修改项'
        }
      case WatermarkApplyStatus.TIMEOUT:
        return {
          text: '应用超时',
          color: 'yellow',
          errorMsg: record.applyShowReason || '应用超时'
        }
      default:
        return {
          text: `状态: ${record.applyStatus}`,
          color: 'gray',
          errorMsg: record.applyShowReason || ''
        }
    }
  }

  // 如果没有应用状态，显示提取状态
  const { text, color } = getExtractionStatusDisplay(record.extractionStatus)
  const errorMsg =
    (record.extractionStatus === WatermarkExtractionStatus.FAILED ||
      record.extractionStatus ===
        WatermarkExtractionStatus.EXTRACTING_TIMEOUT) &&
    record.errorMsg
      ? record.errorMsg
      : undefined

  return { text, color, errorMsg }
}
